import { apiService } from './api.service';

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Interface for Telemetry user properties
interface TelemetryUserProperties {
  userId?: string;
  email?: string;
  name?: string;
  tenantId?: string;
  teamId?: string | null;
  isAdmin?: boolean;
  isInTeams?: boolean;
  isDevelopment?: boolean;
  [key: string]: any; // Allow additional properties
}

// Interface for Telemetry event options
interface TelemetryEventOptions {
  properties?: Record<string, any>;
  groups?: Record<string, string>;
}

/**
 * Telemetry Analytics Service
 * Handles user tracking and event logging via back-end API calls
 */
class TelemetryService {
  private static instance: TelemetryService;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private debugMode = false;
  private telemetryEnabledCache: boolean | null = null;
  private telemetryEnabledCacheTime: number = 0;
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
  private silentMode = true; // Suppress console logs by default

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance of TelemetryService
   */
  public static getInstance(): TelemetryService {
    if (!TelemetryService.instance) {
      TelemetryService.instance = new TelemetryService();
    }
    return TelemetryService.instance;
  }

  /**
    * Log a message if not in silent mode
    */
  private log(message: string, ...args: any[]): void {
    if (!this.silentMode) {
      console.log(message, ...args);
    }
  }

  /**
    * Log an error if not in silent mode
    */
  private logError(message: string, ...args: any[]): void {
    if (!this.silentMode) {
      console.error(message, ...args);
    }
  }

  /**
    * Log a warning if not in silent mode
    */
  private logWarn(message: string, ...args: any[]): void {
    if (!this.silentMode) {
      console.warn(message, ...args);
    }
  }

  /**
    * Check if telemetry is enabled for the current user/tenant
    * Uses caching to avoid repeated API calls
    */
  private async isTelemetryEnabled(): Promise<boolean> {
    const now = Date.now();
    
    // Return cached value if still valid
    if (this.telemetryEnabledCache !== null && (now - this.telemetryEnabledCacheTime) < this.CACHE_DURATION_MS) {
      this.log('[TelemetryService] Using cached telemetry preference');
      return this.telemetryEnabledCache;
    }

    try {
      // Call backend API to check telemetry preference
      const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/preferences`;
      const response = await fetch(url);
      const data = await response.json();

      let enabled = true; // Default to true
      if (data.success && data.data !== undefined) {
        enabled = data.data.telemetryEnabled ?? true; // Default to true if undefined
      }

      // Cache the result
      this.telemetryEnabledCache = enabled;
      this.telemetryEnabledCacheTime = now;

      return enabled;
    } catch (error) {
      this.logError('[TelemetryService] Error checking telemetry preference:', error);
      // If API call fails, disable telemetry to respect user privacy
      return false;
    }
  }

  /**
    * Initialize telemetry service (no longer needs API key on front-end)
    */
  public initialize(debug: boolean = false): Promise<void> {
    if (this.initialized) {
      this.log('[TelemetryService] Already initialized');
      return Promise.resolve();
    }

    // Return the same promise if already in progress
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.debugMode = debug;
    // Set silent mode based on debug flag (silent by default, verbose in debug mode)
    this.silentMode = !debug;

    // Store the initialization promise so concurrent calls wait for the same operation
    this.initializationPromise = (async () => {
      try {
        this.log('[TelemetryService] Initializing telemetry service');

        // Check if back-end telemetry service is available
        const available = await this.checkStatus();
        
        if (available) {
          this.log('[TelemetryService] Back-end telemetry service is available');
          this.initialized = true;
        } else {
          this.logWarn('[TelemetryService] Back-end telemetry service is not available');
          this.initialized = false;
        }
      } catch (error) {
        this.logError('[TelemetryService] Error initializing telemetry service:', error);
        this.initialized = false;
      }
    })();

    return this.initializationPromise;
  }

  /**
    * Check if the back-end telemetry service is available
    */
  private async checkStatus(): Promise<boolean> {
    try {
      const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/status`;
      const response = await fetch(url);
      const data = await response.json();
      return data.success && data.data?.initialized;
    } catch (error) {
      this.logError('[TelemetryService] Error checking status:', error);
      return false;
    }
  }

  /**
    * Identify the current user
    */
  public async identify(userProperties: TelemetryUserProperties): Promise<void> {
    if (!this.initialized) {
      this.logWarn('[TelemetryService] Service not initialized, cannot identify user');
      return;
    }

    try {
      // Check if telemetry is enabled
      const telemetryEnabled = await this.isTelemetryEnabled();
      if (!telemetryEnabled) {
        this.log('[TelemetryService] Telemetry is disabled, skipping identify');
        return;
      }

      this.log('[TelemetryService] Identifying user:', userProperties);

      const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/identify`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userProperties }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to identify user');
      }

      this.log('[TelemetryService] User identified successfully');
    } catch (error) {
      this.logError('[TelemetryService] Error identifying user:', error);
    }
  }

  /**
    * Capture an event
    */
  public async capture(eventName: string, distinctId: string, options: TelemetryEventOptions = {}): Promise<void> {
    if (!this.initialized) {
      this.logWarn('[TelemetryService] Service not initialized, cannot capture event:', eventName);
      return;
    }

    try {
      // Check if telemetry is enabled
      const telemetryEnabled = await this.isTelemetryEnabled();
      if (!telemetryEnabled) {
        this.log('[TelemetryService] Telemetry is disabled, skipping capture event:', eventName);
        return;
      }

      const { properties = {}, groups = {} } = options;

      this.log('[TelemetryService] Capturing event:', eventName, { distinctId, properties, groups });

      const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/capture`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ eventName, distinctId, properties, groups }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to capture event');
      }

      this.log('[TelemetryService] Event captured successfully');
    } catch (error) {
      this.logError('[TelemetryService] Error capturing event:', error);
    }
  }

  /**
    * Track page view
    */
  public async trackPageView(pageName: string, distinctId: string, properties: Record<string, any> = {}): Promise<void> {
    if (!this.initialized) {
      this.logWarn('[TelemetryService] Service not initialized, cannot track page view:', pageName);
      return;
    }

    try {
      // Check if telemetry is enabled
      const telemetryEnabled = await this.isTelemetryEnabled();
      if (!telemetryEnabled) {
        this.log('[TelemetryService] Telemetry is disabled, skipping track page view:', pageName);
        return;
      }

      this.log('[TelemetryService] Tracking page view:', pageName, { distinctId, properties });

      // Add current URL to properties
      // Use virtualUrl if provided, otherwise use window.location.href
      const pageUrl = (properties as any).virtualUrl || window.location.href;
      const enrichedProperties = {
        ...properties,
        $current_url: pageUrl,
      };

      // Remove virtualUrl from final properties
      delete (enrichedProperties as any).virtualUrl;

      const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/pageview`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pageName, distinctId, properties: enrichedProperties }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to track page view');
      }

      this.log('[TelemetryService] Page view tracked successfully');
    } catch (error) {
      this.logError('[TelemetryService] Error tracking page view:', error);
    }
  }

  /**
    * Set user properties
    */
  public async setUserProperties(distinctId: string, properties: Record<string, any>): Promise<void> {
    if (!this.initialized) {
      this.logWarn('[TelemetryService] Service not initialized, cannot set user properties');
      return;
    }

    try {
      // Check if telemetry is enabled
      const telemetryEnabled = await this.isTelemetryEnabled();
      if (!telemetryEnabled) {
        this.log('[TelemetryService] Telemetry is disabled, skipping set user properties');
        return;
      }

      this.log('[TelemetryService] Setting user properties:', { distinctId, properties });

      const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/user-properties`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ distinctId, properties }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to set user properties');
      }

      this.log('[TelemetryService] User properties set successfully');
    } catch (error) {
      this.logError('[TelemetryService] Error setting user properties:', error);
    }
  }

  /**
    * Reset telemetry service (useful when user logs out)
    */
  public reset(): void {
    this.log('[TelemetryService] Resetting telemetry service');
    this.initialized = false;
    this.debugMode = false;
    this.telemetryEnabledCache = null;
    this.telemetryEnabledCacheTime = 0;
    this.silentMode = true; // Reset to silent mode
  }

  /**
    * Check if telemetry service is initialized
    */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
    * Enable debug mode
    */
  public enableDebug(): void {
    this.debugMode = true;
    this.silentMode = false; // Enable logging in debug mode
    this.log('[TelemetryService] Debug mode enabled');
  }

  /**
    * Disable debug mode
    */
  public disableDebug(): void {
    this.debugMode = false;
    this.silentMode = true; // Disable logging when debug mode is off
    this.log('[TelemetryService] Debug mode disabled');
  }
}

// Export singleton instance
export const telemetryService = TelemetryService.getInstance();
